import { Injectable, Inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, exhaustMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthenticationService } from '../../core/services/auth.service';
import { logins, loginSuccess, loginFailure, logout, logoutSuccess, enable2Fa, enable2FaSuccess, enable2FaFailure, passwordSetup, passwordSetupSuccess, passwordSetupFailure, forgotPassword, forgotPasswordSuccess, forgotPasswordFailure, resetPassword, resetPasswordSuccess, resetPasswordFailure, applyLogins, applyLoginSuccess, applyLoginFailure, loginLinkAccess, loginLinkAccessFailure, loginLinkAccessSuccess, backToPortal, backToPortalSuccess, currentUser, currentUserSuccess, currentUserFailure } from './authentication.actions';
import { environment } from 'src/environments/environment';
import { ForgotEmail, PasswordSetup, resetPasswordData, TwoFactorCode } from 'src/app/modals/Authentication/auth.models';
import { AlertService } from 'src/app/core/services/alert.service';
import { RouterService } from 'src/app/core/services/router.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { CrudServerService } from 'src/app/core/services/crud-server.service';
import { Store } from '@ngrx/store';
import { RootReducerState } from '..';
import * as Sentry from '@sentry/angular';

@Injectable()
export class AuthenticationEffects {

  backend: string = environment.backend;

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logins),
      exhaustMap(({ email, password }) => {
        return this.AuthenticationService.logins(email, password).pipe(

          map((response) => {

            if(response.data){
              const user = this.proceedLogin(response);

              //Redirect user
              this.routeService.triggerRoute(response, "/");

              return loginSuccess({ user });
            }
            
          }),

          catchError((error) =>
            of(error).pipe(
              tap(() => {
                this.alertService.triggerAlert(error, "error");
              }),
              map(() => loginFailure({ error })) // Dispatch loginFailure
            )
          )
        
        );
      })
    )
  );

  proceedLogin(response){
    this.AuthenticationService.resetAll();
    Sentry.setUser(null);
    const user = response.data;

    if(user.qrcode){
      this.localStorageService.setItem('qrCode', user.qrcode);
      this.localStorageService.setItem('two_factor_backup_codes', user.two_factor_backup_codes);
    }else{
      this.localStorageService.removeItem('qrCode');
      this.localStorageService.removeItem('two_factor_backup_codes');
    }

    this.AuthenticationService.pushUserData(user);
    this.AuthenticationService.setToken();

    return user;
  }

  applyLogins$ = createEffect(() =>
    this.actions$.pipe(
      ofType(applyLogins),
      exhaustMap(({ urlData }) => {
        let urlDataString = "profile-setup-contributie"+urlData;
        return this.AuthenticationService.applyLogin(urlDataString).pipe(

          map((response) => {
            
            if(response.data){
              const user = this.proceedLogin(response);
              
              //Redirect user
              this.routeService.triggerRoute(response, "/");
              
              return applyLoginSuccess({ user });
            }
            
          }),

          // catchError((error) => of(loginFailure({ error })), // Closing parenthesis added here
          catchError((error) =>
            of(error).pipe(
              tap(() => {
                this.routeService.triggerRoute(null, "/");
              }),
              map(() => applyLoginFailure({ error })) // Dispatch loginFailure
            )
          )
        
        );
      })
    )
  );

  loginLinkAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginLinkAccess),
      exhaustMap(({ urlData }) => {
        let urlDataString = "login-link-access"+urlData;
        return this.AuthenticationService.applyLogin(urlDataString).pipe(

          map((response) => {

            if(response.data){

              this.proceedLogin(response);

              //Setup token
              let userDetails = this.localStorageService.getItem('currentUser');
              userDetails["has_back_link"] = true;
              this.localStorageService.setItem('currentUser', userDetails);
              
              //Redirect user
              this.routeService.triggerRoute(response, "/");
              
              return loginLinkAccessSuccess({ user: response.data });
            }
            
          }),

          // catchError((error) => of(loginFailure({ error })), // Closing parenthesis added here
          catchError((error) =>
            of(error).pipe(
              tap(() => {
                this.routeService.triggerRoute(null, "/");
              }),
              map(() => loginLinkAccessFailure({ error })) // Dispatch loginFailure
            )
          )
        
        );
      })
    )
  );

  backToPortalLink: string = "";
  backToPortal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backToPortal),
      exhaustMap( () => {

        const url = "back-to-portal";
        return this.crudService.fetchData(url).pipe(

          map((response) => {
            const data = response.data;
            this.AuthenticationService.resetAll();
            Sentry.setUser(null);
            
            this.store.dispatch(logout());
            this.backToPortalLink = data.link;
            
            return backToPortalSuccess();
          }),

          catchError((error) =>
            of(error).pipe(
              tap(() => {
                this.AuthenticationService.resetAll();
                Sentry.setUser(null);
                this.routeService.triggerRoute(null, "auth/login");
              }),
            )
          )
        
        );
      })
    )
  );
  
  enable2Fa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(enable2Fa),
      exhaustMap( ({otp}) => {

        const twoFactorCode: TwoFactorCode = {
                                                  code: otp
                                              };
      
        return this.AuthenticationService.enable2Fa(twoFactorCode).pipe(

          map((response) => {

            // this.localStorageService.removeItem('qrCode');
            // this.localStorageService.removeItem('two_factor_backup_codes');
            this.alertService.triggerAlert(response.message, "success");
            this.localStorageService.setItem('2fa_status', "3");
            this.routeService.triggerRoute(response, "/");
            return enable2FaSuccess();
            
          }),

          catchError((error) =>
            of(error).pipe(
              tap(() => {
                this.alertService.triggerAlert(error, "error");
              }),
              map(() => enable2FaFailure({ error })) // Dispatch loginFailure
            )
          )
        
        );
      })
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      exhaustMap( () => {

        return this.AuthenticationService.logout().pipe(

          map((response) => {

            this.AuthenticationService.resetAll();
            Sentry.setUser(null);
            // this.alertService.triggerAlert(response.message, "success");
            this.routeService.triggerRoute(null, "auth/login");
            if(this.backToPortalLink !== ""){
              window.location.replace(this.backToPortalLink);
              this.backToPortalLink = "";
            }
            
            return logoutSuccess();
            
          }),

          catchError((error) =>
            of(error).pipe(
              tap(() => {
                this.AuthenticationService.resetAll();
                Sentry.setUser(null);
                this.routeService.triggerRoute(null, "auth/login");
                // this.alertService.triggerAlert(error, "error");
              }),
              // map(() => enable2FaFailure({ error })) // Dispatch loginFailure
            )
          )
        
        );
      })
    )
  );
  
  passwordSetup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(passwordSetup),
      exhaustMap( ({password}) => {
        const passwordSetup: PasswordSetup = {
                                                password: password
                                            };
      
        return this.AuthenticationService.passwordSetup(passwordSetup).pipe(

          map((response) => {

            this.alertService.triggerAlert(response.message, "success");
            this.localStorageService.setItem('2fa_status', "0");
            this.routeService.triggerRoute(response, "two-factor-enable");
            return passwordSetupSuccess();
            
          }),

          catchError((error) =>
            of(error).pipe(
              tap(() => {
                this.alertService.triggerAlert(error, "error");
              }),
              map(() => passwordSetupFailure({ error })) // Dispatch loginFailure
            )
          )
        
        );
      })
    )
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forgotPassword),
      exhaustMap( ({email}) => {
        const forgotEmail: ForgotEmail = {
                                          email: email
                                        };
    
        return this.AuthenticationService.forgotPassword(forgotEmail).pipe(

          map((response) => {
            this.alertService.triggerAlert(response.message, "success");
            this.routeService.triggerRoute(response, "/auth/login");
            return forgotPasswordSuccess();
            
          }),

          catchError((error) =>
            of(error).pipe(
              tap(() => {
                this.alertService.triggerAlert(error, "error");
              }),
              map(() => forgotPasswordFailure({ error })) // Dispatch 
            )
          )
        
        );
      })
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetPassword),
      exhaustMap( ({token, email, password}) => {
        const resetPasswordData: resetPasswordData = {
                                          token: token,
                                          email: email,
                                          password: password
                                        };
    
        return this.AuthenticationService.resetPassword(resetPasswordData).pipe(
          map((response) => {
            this.alertService.triggerAlert(response.message, "success");
            this.routeService.triggerRoute(response, "/auth/login");
            return resetPasswordSuccess();
          }),

          catchError((error) =>
            of(error).pipe(
              tap(() => {
                this.alertService.triggerAlert(error, "error");
              }),
              map(() => resetPasswordFailure({ error })) // Dispatch 
            )
          )
        );
      })
    )
  );

  currentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(currentUser),
      exhaustMap( () => {
        
        const url = 'fetch-user';
        return this.crudService.fetchData(url).pipe(

          map((response) => {
            if(response.data){
              const user = response.data;

              if(user.qrcode){
                this.localStorageService.setItem('qrCode', user.qrcode);
                this.localStorageService.setItem('two_factor_backup_codes', user.two_factor_backup_codes);
              }else{
                this.localStorageService.removeItem('qrCode');
                this.localStorageService.removeItem('two_factor_backup_codes');
              }

              this.AuthenticationService.pushUserData(response.data);
              return currentUserSuccess({ user: user });
            }
          }),

          catchError((error) =>
            of(error).pipe(
              tap(() => {
                // this.alertService.triggerAlert(error, "error");
              }),
              map(() => currentUserFailure({ error })) // Dispatch loginFailure
            )
          )
        
        );
    
      })
    )
  );
  

  constructor(
    @Inject(Actions) private actions$: Actions,
    private AuthenticationService: AuthenticationService,
    private routeService: RouterService,
    private alertService: AlertService,
    private localStorageService: LocalStorageService,
    private crudService: CrudServerService,
    public store: Store<RootReducerState>,
  ) { }

}